import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

const buttonVariants = cva(
  cn(
    /* sorted */
    "inline-flex items-center justify-center gap-2",
    "text-sm font-medium whitespace-nowrap",
    "rounded-full outline-none cursor-pointer",
    "transition-[color,box-shadow]",
    // `aria-disabled` covers `asChild` + a disabled TanStack `Link`: it renders an
    // anchor, which never matches `:disabled`, so the styling has to hang off ARIA.
    "disabled:pointer-events-none aria-disabled:pointer-events-none",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5 [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        primary: "text-text-primary",
        neutral: "text-text-dark-max",
        negative: "text-text-negative",
      },
      kind: {
        solid: cn(
          /* sorted */
          "text-text-light",
          "disabled:bg-bg-dark-min disabled:text-text-dark-min",
          "aria-disabled:bg-bg-dark-min aria-disabled:text-text-dark-min",
        ),
        soft: cn(
          /* sorted */
          "disabled:bg-bg-dark-min disabled:text-text-dark-min",
          "aria-disabled:bg-bg-dark-min aria-disabled:text-text-dark-min",
        ),
        outline: cn(
          /* sorted */
          "bg-bg-transparent",
          "border border-border-dark-low",
          "disabled:bg-bg-dark-min disabled:text-text-dark-min",
          "aria-disabled:bg-bg-dark-min aria-disabled:text-text-dark-min",
        ),
        ghost: cn(
          /* sorted */
          "disabled:bg-bg-dark-min disabled:text-text-dark-min",
          "aria-disabled:bg-bg-dark-min aria-disabled:text-text-dark-min",
        ),
        link: "underline",
      },
      size: {
        xs: "py-0.5 px-3 gap-1 h-6 text-sm font-normal",
        sm: "py-1 px-3 gap-2 h-7 font-normal",
        md: "py-1.5 px-4 gap-2 h-8",
        lg: "py-2 px-4 gap-2 h-9",
        xl: "py-2.5 px-5 gap-2 h-10",
        "icon-xs": "h-6 w-6 shrink-0",
        "icon-sm": "h-7 w-7 shrink-0",
        "icon-md": "h-8 w-8 shrink-0",
        "icon-lg": "h-9 w-9 shrink-0",
        "icon-xl": "h-10 w-10 shrink-0",
      },
    },
    compoundVariants: [
      // === SOLID ===
      {
        kind: "solid",
        variant: "primary",
        class: "bg-bg-primary-max hover:bg-bg-primary-max-hover",
      },
      {
        kind: "solid",
        variant: "neutral",
        class: "bg-bg-dark-max hover:bg-bg-dark-max-hover",
      },
      {
        kind: "solid",
        variant: "negative",
        class: "bg-bg-negative-max hover:bg-bg-negative-max-hover",
      },
      // === SOFT ===
      {
        kind: "soft",
        variant: "primary",
        class: "bg-bg-primary-min hover:bg-bg-primary-min-hover",
      },
      {
        kind: "soft",
        variant: "neutral",
        class: "bg-bg-dark-min hover:bg-bg-dark-min-hover",
      },
      {
        kind: "soft",
        variant: "negative",
        class: "bg-bg-negative-min hover:bg-bg-negative-min-hover",
      },
      // === OUTLINE ===
      {
        kind: "outline",
        variant: "primary",
        class: "hover:bg-bg-primary-min",
      },
      {
        kind: "outline",
        variant: "neutral",
        class: "hover:bg-bg-dark-min",
      },
      {
        kind: "outline",
        variant: "negative",
        class: "hover:bg-bg-negative-min",
      },
      // === GHOST ===
      {
        kind: "ghost",
        variant: "primary",
        class: "hover:bg-bg-primary-min",
      },
      {
        kind: "ghost",
        variant: "neutral",
        class: "hover:bg-bg-dark-min data-[state=open]:bg-bg-dark-min",
      },
      {
        kind: "ghost",
        variant: "negative",
        class: "hover:bg-bg-negative-min",
      },
      // === LINK ===
      {
        kind: "link",
        variant: "neutral",
        class: "hover:bg-none p-0",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      kind: "solid",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({
  className,
  variant,
  kind,
  size,
  type = "button",
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ kind, size, variant }), className)}
      type={type}
      {...props}
    />
  );
}

export type { ButtonProps };
export { Button, buttonVariants };
